// Servicio de autenticación — OAuth2 client_credentials: gestiona token y sesión.
import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, finalize, map, shareReplay } from 'rxjs';
import { StorageService } from '../storage/storage.service';
import { API_ENDPOINTS } from '../../config/api.config';
import { environment } from '../../../../../environments/environment';
import { AuthToken, TokenResponse } from '../../../domain/interfaces/auth/auth.interface';

// Margen de refresco proactivo: renovar 60 s antes de expirar
const REFRESH_BUFFER_MS = 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(StorageService);

  // sessionId generado una única vez por carga de la app, independiente del token.
  private readonly sessionId = Date.now().toString();

  // Estado del token con Signal
  private readonly _token = signal<AuthToken | null>(this.loadStoredToken());
  readonly token = this._token.asReadonly();

  // Token expuesto en dos formas (compatibilidad Signals + RxJS)
  readonly accessToken = computed(() => this._token()?.accessToken ?? '');
  readonly accessToken$ = toObservable(this.accessToken);
  readonly isAuthenticated = computed(() => this.accessToken().length > 0);

  // Thundering-herd prevention: observable compartido mientras hay un fetch en curso
  private inFlightFetch$: Observable<AuthToken> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  /** Verifica si el token actual es válido (con margen de refresco proactivo) */
  isTokenValid(): boolean {
    const token = this._token();
    if (!token) return false;
    const expiryTime = token.issuedAt + token.expiresIn * 1000;
    return Date.now() < expiryTime - REFRESH_BUFFER_MS;
  }

  /** Obtiene el token de acceso para adjuntar en headers */
  getAccessToken(): string | null {
    return this._token()?.accessToken ?? null;
  }

  /** Obtiene el ID de sesión (fijo por carga de app, no viene del backend) */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Solicita un nuevo token al backend (POST form-urlencoded, client_credentials).
   * Comparte un único observable entre requests concurrentes para evitar
   * múltiples llamadas simultáneas (thundering herd).
   */
  fetchToken(): Observable<AuthToken> {
    if (this.inFlightFetch$) return this.inFlightFetch$;

    const body = new HttpParams()
      .set('client_id', environment.auth.clientId)
      .set('grant_type', environment.auth.grantType)
      .set('scope', environment.auth.scope)
      .set('client_secret', environment.auth.clientSecret);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      Accept: '*/*',
    });

    this.inFlightFetch$ = this.http
      .post<TokenResponse>(API_ENDPOINTS.auth.token, body.toString(), { headers })
      .pipe(
        map((response) => this.storeToken(response)),
        finalize(() => {
          this.inFlightFetch$ = null;
        }),
        shareReplay(1),
      );

    return this.inFlightFetch$;
  }

  /** Limpia el token al cerrar sesión */
  clearToken(): void {
    this._token.set(null);
    this.storage.removeLocal(environment.auth.cookieKey);
    this.storage.removeLocal(environment.auth.cookieExpiryDateKey);
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /** Mapea la respuesta cruda del backend, persiste y publica el token */
  private storeToken(response: TokenResponse): AuthToken {
    const token: AuthToken = {
      accessToken: response.access_token,
      expiresIn: response.expires_in,
      issuedAt: Date.now(),
    };

    this._token.set(token);

    const expiryDate = new Date(token.issuedAt + token.expiresIn * 1000);
    this.storage.setLocal(environment.auth.cookieKey, token.accessToken);
    this.storage.setLocal(environment.auth.cookieExpiryDateKey, expiryDate.toISOString());

    this.setRefreshTimer(token.expiresIn);

    return token;
  }

  /** Programa el refresco proactivo del token 60s antes de que expire */
  private setRefreshTimer(expiresIn: number): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    const delayMs = Math.max(expiresIn - 60, 0) * 1000;
    this.refreshTimer = setTimeout(() => this.fetchToken().subscribe(), delayMs);
  }

  /** Carga el token persistido en localStorage si existe y aún no expiró */
  private loadStoredToken(): AuthToken | null {
    const accessToken = this.storage.getLocal<string>(environment.auth.cookieKey);
    const expiryIso = this.storage.getLocal<string>(environment.auth.cookieExpiryDateKey);
    if (!accessToken || !expiryIso) return null;

    const expiryTime = new Date(expiryIso).getTime();
    if (Number.isNaN(expiryTime)) return null;

    const now = Date.now();
    if (now >= expiryTime) return null;


    return {
      accessToken,
      expiresIn: Math.round((expiryTime - now) / 1000),
      issuedAt: now,
    };
  }
}
